import Phaser from 'phaser';
import { PLANTS, SHOP_ITEMS } from '../data/plants';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, FARM_SIZE } from '../data/world-config';
import { TouchControls } from '../ui/TouchControls';
import type { GameScene } from './GameScene';

export class UIScene extends Phaser.Scene {
  private gameScene!: GameScene;
  private inventoryContainer!: Phaser.GameObjects.Container;
  private inventoryVisible: boolean = false;
  private shopContainer!: Phaser.GameObjects.Container;
  private shopVisible: boolean = false;
  private timeDisplay!: Phaser.GameObjects.Text;
  private dayCountDisplay!: Phaser.GameObjects.Text;
  private hotbar!: Phaser.GameObjects.Container;
  private tooltipText!: Phaser.GameObjects.Text;
  private notificationText!: Phaser.GameObjects.Text;
  private notificationTween?: Phaser.Tweens.Tween;
  touchControls!: TouchControls;

  // Combat HUD
  private hpBarGraphics!: Phaser.GameObjects.Graphics;
  private hpText!: Phaser.GameObjects.Text;
  private coinText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private xpBarGraphics!: Phaser.GameObjects.Graphics;
  private energyBarGraphics!: Phaser.GameObjects.Graphics;
  private energyText!: Phaser.GameObjects.Text;

  // Combo counter
  private comboContainer!: Phaser.GameObjects.Container;
  private comboCountText!: Phaser.GameObjects.Text;
  private comboLabelText!: Phaser.GameObjects.Text;
  private comboFadeTween?: Phaser.Tweens.Tween;

  // Move list overlay
  private moveListContainer!: Phaser.GameObjects.Container;
  private moveListVisible: boolean = false;

  // Minimap
  private minimapContainer!: Phaser.GameObjects.Container;
  private minimapGraphics!: Phaser.GameObjects.Graphics;
  private minimapPlayerDot!: Phaser.GameObjects.Arc;
  private minimapVisible: boolean = true;
  private minimapSize: number = 100;
  private minimapScale: number = 0; // calculated in createMinimap

  // Multiplayer connection status
  private connectionStatusText?: Phaser.GameObjects.Text;
  private connectionDot?: Phaser.GameObjects.Arc;

  constructor() {
    super({ key: 'UIScene' });
  }

  init(data: { gameScene: GameScene }): void {
    this.gameScene = data.gameScene;
  }

  /** Scale factor for UI elements on small screens. */
  private uiScale: number = 1;

  create(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Detect small screen and scale UI up (with RESIZE mode, canvas = viewport)
    this.uiScale = Math.min(width, height) < 500 ? 1.35 : 1;

    // Create hotbar at bottom center
    this.createHotbar(width, height);

    // Time/day display at top right
    this.createTimeDisplay(width);

    // Inventory panel (hidden by default)
    this.createInventoryPanel(width, height);

    // Tooltip text (for hovering over items)
    this.tooltipText = this.add.text(0, 0, '', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 4, y: 2 },
    });
    this.tooltipText.setVisible(false);
    this.tooltipText.setDepth(200);

    // Notification text (for events)
    const notifFontSize = this.uiScale > 1 ? '18px' : '14px';
    this.notificationText = this.add.text(width / 2, 60, '', {
      fontFamily: 'monospace',
      fontSize: notifFontSize,
      color: '#e8a038',
      stroke: '#000000',
      strokeThickness: 3,
    });
    this.notificationText.setOrigin(0.5, 0.5);
    this.notificationText.setAlpha(0);
    this.notificationText.setDepth(200);

    // Shop panel (hidden by default)
    this.createShopPanel(width, height);

    // Listen for game events
    this.gameScene.events.on('toggle-inventory', () => this.toggleInventory());
    this.gameScene.events.on('open-shop', () => this.toggleShop());
    this.gameScene.events.on('notification', (msg: string) => this.showNotification(msg));
    this.gameScene.events.on('time-update', (data: { timeOfDay: number; dayCount: number; isNight: boolean }) => {
      this.updateTimeDisplay(data);
    });

    // Keyboard shortcuts for hotbar
    for (let i = 0; i < 8; i++) {
      const key = this.input.keyboard!.addKey(49 + i); // Keys 1-8
      key.on('down', () => this.selectHotbarSlot(i));
    }

    // Touch controls (virtual joystick + action buttons) — only on touch devices
    this.touchControls = new TouchControls(
      this,
      () => this.gameScene.handleInteractPublic(),
      () => this.gameScene.events.emit('toggle-inventory'),
      () => this.gameScene.handleAttackPublic(),
      () => this.gameScene.handleSentinelPublic(),
    );

    // Combat HUD (top left)
    this.createCombatHUD();

    // Combo counter (center screen)
    this.createComboCounter(width, height);

    // Move list overlay (toggled with M)
    this.createMoveList(width, height);

    // Listen for combat stats updates
    this.gameScene.events.on('combat-stats-update', (stats: {
      hp: number; maxHp: number; coins: number; xp: number;
      xpToNext: number; level: number; swordLevel: number; swordDamage: number;
      energy: number; maxEnergy: number; comboCount: number; isBlocking: boolean;
    }) => {
      this.updateCombatHUD(stats);
    });

    // Listen for combo updates
    this.gameScene.events.on('combo-update', (data: { count: number; type: string }) => {
      this.updateComboCounter(data.count, data.type);
    });

    // Multiplayer connection status indicator
    if (this.gameScene.isMultiplayer) {
      this.createConnectionStatus(width);
      this.gameScene.events.on('connection-state', (state: string) => {
        this.updateConnectionStatus(state);
      });
    }

    // Minimap (top-right, below time)
    this.createMinimap(width);

    // M key for move list
    const mKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.M);
    mKey.on('down', () => this.toggleMoveList());

    // Tab key to toggle minimap
    const tabKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TAB);
    tabKey.on('down', () => this.toggleMinimap());

    // Handle viewport resize — reposition all HUD elements
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      const w = gameSize.width;
      const h = gameSize.height;
      this.cameras.main.setSize(w, h);

      // Hotbar → bottom center
      if (this.hotbar) this.hotbar.setPosition(w / 2, h - 30);
      // Notification → top center
      if (this.notificationText) this.notificationText.setPosition(w / 2, 60);
      // Time display → top right
      if (this.timeDisplay) this.timeDisplay.setPosition(w - 60, 10);
      if (this.dayCountDisplay) this.dayCountDisplay.setPosition(w - 60, 28);
      // Minimap → top right, below time
      if (this.minimapContainer) {
        this.minimapContainer.setPosition(w - this.minimapSize - 10, 50);
      }
      // Inventory → center
      if (this.inventoryContainer) this.inventoryContainer.setPosition(w / 2, h / 2);
    });
  }

  update(): void {
    this.updateHotbar();
    this.updateCombatHUDFromScene();
    this.updateMinimap();
  }

  private createHotbar(width: number, height: number): void {
    this.hotbar = this.add.container(width / 2, height - 30);
    const slotSize = 28;
    const slotCount = 8;
    const totalWidth = slotCount * (slotSize + 2);
    const startX = -totalWidth / 2;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.6);
    bg.fillRoundedRect(startX - 4, -slotSize / 2 - 4, totalWidth + 8, slotSize + 8, 4);
    this.hotbar.add(bg);

    for (let i = 0; i < slotCount; i++) {
      const x = startX + i * (slotSize + 2) + slotSize / 2;

      // Slot background
      const slotBg = this.add.graphics();
      slotBg.fillStyle(0x333333, 0.8);
      slotBg.fillRect(x - slotSize / 2, -slotSize / 2, slotSize, slotSize);
      slotBg.lineStyle(1, 0x666666);
      slotBg.strokeRect(x - slotSize / 2, -slotSize / 2, slotSize, slotSize);
      this.hotbar.add(slotBg);

      // Item icon (will be updated)
      const icon = this.add.sprite(x, 0, 'plants', 0);
      icon.setVisible(false);
      icon.setName(`hotbar-icon-${i}`);
      this.hotbar.add(icon);

      // Quantity text
      const qty = this.add.text(x + slotSize / 2 - 2, slotSize / 2 - 2, '', {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
      });
      qty.setOrigin(1, 1);
      qty.setName(`hotbar-qty-${i}`);
      this.hotbar.add(qty);

      // Key number
      const keyLabel = this.add.text(x - slotSize / 2 + 2, -slotSize / 2 + 1, `${i + 1}`, {
        fontFamily: 'monospace',
        fontSize: '7px',
        color: '#999999',
      });
      this.hotbar.add(keyLabel);
    }

    // Selection indicator
    const selector = this.add.graphics();
    selector.lineStyle(2, 0xe8a038);
    selector.strokeRect(-slotSize / 2, -slotSize / 2, slotSize, slotSize);
    selector.setName('hotbar-selector');
    selector.setPosition(startX + slotSize / 2, 0);
    this.hotbar.add(selector);

    this.hotbar.setDepth(100);
    this.hotbar.setScale(this.uiScale);
  }

  private updateHotbar(): void {
    if (!this.gameScene.player) return;

    const inventory = this.gameScene.player.inventory;
    const slotSize = 28;
    const slotCount = 8;
    const totalWidth = slotCount * (slotSize + 2);
    const startX = -totalWidth / 2;

    for (let i = 0; i < slotCount; i++) {
      const icon = this.hotbar.getByName(`hotbar-icon-${i}`) as Phaser.GameObjects.Sprite;
      const qty = this.hotbar.getByName(`hotbar-qty-${i}`) as Phaser.GameObjects.Text;

      if (i < inventory.length) {
        const item = inventory[i];
        const plant = PLANTS[item.plantId];
        if (plant) {
          icon.setTexture('plants');
          icon.setFrame(plant.spriteRow * 4 + 3);
          icon.setVisible(true);
          icon.setTint(0xffffff);
          qty.setText(`${item.quantity}`);
        } else if (item.plantId === '_fertilizer') {
          icon.setTexture('objects');
          icon.setFrame(7);
          icon.setVisible(true);
          icon.setTint(0x88ff44);
          qty.setText(`${item.quantity}`);
        } else if (item.plantId === '_sprinkler') {
          icon.setTexture('objects');
          icon.setFrame(5);
          icon.setVisible(true);
          icon.setTint(0x6eb5ff);
          qty.setText(`${item.quantity}`);
        } else {
          icon.setVisible(false);
          qty.setText('');
        }
      } else {
        icon.setVisible(false);
        qty.setText('');
      }
    }

    // Update selector position
    const selector = this.hotbar.getByName('hotbar-selector') as Phaser.GameObjects.Graphics;
    const selectedSlot = this.gameScene.player.selectedSlot;
    selector.setPosition(startX + selectedSlot * (slotSize + 2) + slotSize / 2, 0);
  }

  private selectHotbarSlot(index: number): void {
    if (this.gameScene.player) {
      this.gameScene.player.selectedSlot = index;
    }
  }

  private createTimeDisplay(width: number): void {
    const container = this.add.container(width - 10, 10);

    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.6);
    bg.fillRoundedRect(-100, -5, 100, 40, 4);
    container.add(bg);

    this.timeDisplay = this.add.text(-50, 2, '6:00 AM', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#e8a038',
    });
    this.timeDisplay.setOrigin(0.5, 0);
    container.add(this.timeDisplay);

    this.dayCountDisplay = this.add.text(-50, 18, 'Day 1', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#aaaaaa',
    });
    this.dayCountDisplay.setOrigin(0.5, 0);
    container.add(this.dayCountDisplay);

    container.setDepth(100);
    container.setScale(this.uiScale);
  }

  private updateTimeDisplay(data: { timeOfDay: number; dayCount: number; isNight: boolean }): void {
    // Convert timeOfDay (0-1) to clock time
    // 0 = 6:00 AM, 0.5 = 6:00 PM, 1.0 = 6:00 AM next day
    const totalMinutes = Math.floor(data.timeOfDay * 24 * 60);
    const hours24 = (6 + Math.floor(totalMinutes / 60)) % 24;
    const minutes = totalMinutes % 60;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    const timeStr = `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;

    this.timeDisplay.setText(timeStr);
    this.timeDisplay.setColor(data.isNight ? '#8b68c4' : '#e8a038');
    this.dayCountDisplay.setText(`Day ${data.dayCount}`);
  }

  private createInventoryPanel(width: number, height: number): void {
    this.inventoryContainer = this.add.container(width / 2, height / 2);
    this.inventoryContainer.setVisible(false);
    this.inventoryContainer.setDepth(150);

    // Panel background
    const panelW = 300;
    const panelH = 250;
    const bg = this.add.graphics();
    bg.fillStyle(0x1a1a2e, 0.95);
    bg.fillRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    bg.lineStyle(2, 0x4a7c4b);
    bg.strokeRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    this.inventoryContainer.add(bg);

    // Title
    const title = this.add.text(0, -panelH / 2 + 15, 'Inventory', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#6aaa3a',
    });
    title.setOrigin(0.5, 0);
    this.inventoryContainer.add(title);

    // Close hint
    const closeHint = this.add.text(0, panelH / 2 - 15, 'Press I to close', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#666666',
    });
    closeHint.setOrigin(0.5, 1);
    this.inventoryContainer.add(closeHint);

    // Close button (tap-friendly for mobile)
    const closeBtnBg = this.add.circle(panelW / 2 - 12, -panelH / 2 + 12, 14, 0xd4573b, 0.9);
    closeBtnBg.setStrokeStyle(1, 0xff6666);
    this.inventoryContainer.add(closeBtnBg);
    const closeX = this.add.text(panelW / 2 - 12, -panelH / 2 + 12, 'X', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    closeX.setOrigin(0.5, 0.5);
    this.inventoryContainer.add(closeX);
    const closeHitZone = this.add.circle(panelW / 2 - 12, -panelH / 2 + 12, 18, 0x000000, 0.001);
    closeHitZone.setInteractive();
    closeHitZone.on('pointerdown', () => this.toggleInventory());
    this.inventoryContainer.add(closeHitZone);
  }

  private toggleInventory(): void {
    this.inventoryVisible = !this.inventoryVisible;
    this.inventoryContainer.setVisible(this.inventoryVisible);

    if (this.inventoryVisible) {
      this.refreshInventoryPanel();
    }
  }

  private refreshInventoryPanel(): void {
    if (!this.gameScene.player) return;

    // Remove old item displays (keep first 3: bg, title, close hint)
    while (this.inventoryContainer.list.length > 3) {
      const child = this.inventoryContainer.list[3];
      if (child instanceof Phaser.GameObjects.GameObject) {
        child.destroy();
      }
      this.inventoryContainer.remove(child);
    }

    const inventory = this.gameScene.player.inventory;
    const slotSize = 32;
    const cols = 5;
    const startX = -(cols * (slotSize + 4)) / 2 + slotSize / 2;
    const startY = -60;

    inventory.forEach((item, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (slotSize + 4);
      const y = startY + row * (slotSize + 4);

      const plant = PLANTS[item.plantId];
      const isSpecial = item.plantId.startsWith('_');
      const displayName = isSpecial ? item.plantId.slice(1) : (plant?.name ?? item.plantId);

      // Slot bg
      const slotBg = this.add.graphics();
      slotBg.fillStyle(isSpecial ? 0x2a3a2a : 0x333333, 0.8);
      slotBg.fillRect(x - slotSize / 2, y - slotSize / 2, slotSize, slotSize);
      slotBg.lineStyle(1, isSpecial ? 0x88ff44 : 0x555555);
      slotBg.strokeRect(x - slotSize / 2, y - slotSize / 2, slotSize, slotSize);
      this.inventoryContainer.add(slotBg);

      // Plant icon or special item icon
      if (plant) {
        const icon = this.add.sprite(x, y, 'plants', plant.spriteRow * 4 + 3);
        icon.setScale(1.5);
        this.inventoryContainer.add(icon);
      } else if (isSpecial) {
        const icon = this.add.text(x, y, item.plantId === '_fertilizer' ? 'F' : 'S', {
          fontFamily: 'monospace', fontSize: '16px', color: item.plantId === '_fertilizer' ? '#88ff44' : '#6eb5ff',
          fontStyle: 'bold',
        });
        icon.setOrigin(0.5, 0.5);
        this.inventoryContainer.add(icon);
      }

      // Quantity
      const qty = this.add.text(x + slotSize / 2 - 2, y + slotSize / 2 - 2, `${item.quantity}`, {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
      });
      qty.setOrigin(1, 1);
      this.inventoryContainer.add(qty);

      // Name below
      const name = this.add.text(x, y + slotSize / 2 + 4, displayName, {
        fontFamily: 'monospace',
        fontSize: '7px',
        color: isSpecial ? '#88ff44' : '#aaaaaa',
      });
      name.setOrigin(0.5, 0);
      this.inventoryContainer.add(name);
    });
  }

  private createCombatHUD(): void {
    const container = this.add.container(10, 10);
    container.setDepth(100);

    // Background (taller to fit energy bar)
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.6);
    bg.fillRoundedRect(0, 0, 140, 78, 4);
    container.add(bg);

    // HP label + bar
    const hpLabel = this.add.text(5, 4, 'HP', {
      fontFamily: 'monospace', fontSize: '8px', color: '#d4573b',
    });
    container.add(hpLabel);

    this.hpBarGraphics = this.add.graphics();
    this.hpBarGraphics.setDepth(100);
    container.add(this.hpBarGraphics);

    this.hpText = this.add.text(135, 4, '100/100', {
      fontFamily: 'monospace', fontSize: '8px', color: '#ffffff',
    });
    this.hpText.setOrigin(1, 0);
    container.add(this.hpText);

    // Energy bar
    const enLabel = this.add.text(5, 16, 'EN', {
      fontFamily: 'monospace', fontSize: '8px', color: '#44aaff',
    });
    container.add(enLabel);

    this.energyBarGraphics = this.add.graphics();
    this.energyBarGraphics.setDepth(100);
    container.add(this.energyBarGraphics);

    this.energyText = this.add.text(135, 16, '100', {
      fontFamily: 'monospace', fontSize: '8px', color: '#44aaff',
    });
    this.energyText.setOrigin(1, 0);
    container.add(this.energyText);

    // XP bar
    const xpLabel = this.add.text(5, 28, 'XP', {
      fontFamily: 'monospace', fontSize: '8px', color: '#e8a038',
    });
    container.add(xpLabel);

    this.xpBarGraphics = this.add.graphics();
    this.xpBarGraphics.setDepth(100);
    container.add(this.xpBarGraphics);

    // Level + Coins
    this.levelText = this.add.text(5, 42, 'Lv.1', {
      fontFamily: 'monospace', fontSize: '9px', color: '#6aaa3a',
    });
    container.add(this.levelText);

    this.coinText = this.add.text(135, 42, '0 coins', {
      fontFamily: 'monospace', fontSize: '9px', color: '#e8a038',
    });
    this.coinText.setOrigin(1, 0);
    container.add(this.coinText);

    // Control hints - fighting game style
    const hint1 = this.add.text(5, 55, 'J=Light K=Heavy L=Special', {
      fontFamily: 'monospace', fontSize: '6px', color: '#888888',
    });
    container.add(hint1);

    const hint2 = this.add.text(5, 63, 'Shift=Block M=Moves B=Shop', {
      fontFamily: 'monospace', fontSize: '6px', color: '#888888',
    });
    container.add(hint2);

    // Draw initial bars
    this.drawHpBar(100, 100);
    this.drawEnergyBar(100, 100);
    this.drawXpBar(0, 50);

    // Scale up HUD on mobile
    container.setScale(this.uiScale);
  }

  private drawHpBar(hp: number, maxHp: number): void {
    const g = this.hpBarGraphics;
    g.clear();
    const x = 20;
    const y = 5;
    const w = 90;
    const h = 8;

    g.fillStyle(0x333333);
    g.fillRect(x, y, w, h);

    const ratio = hp / maxHp;
    const color = ratio > 0.5 ? 0x6aaa3a : ratio > 0.25 ? 0xe8a038 : 0xd4573b;
    g.fillStyle(color);
    g.fillRect(x, y, w * ratio, h);

    g.lineStyle(1, 0x666666);
    g.strokeRect(x, y, w, h);
  }

  private drawEnergyBar(energy: number, maxEnergy: number): void {
    const g = this.energyBarGraphics;
    g.clear();
    const x = 20;
    const y = 17;
    const w = 90;
    const h = 7;

    g.fillStyle(0x222233);
    g.fillRect(x, y, w, h);

    const ratio = maxEnergy > 0 ? energy / maxEnergy : 0;
    // Pulse color when low
    const color = ratio > 0.3 ? 0x44aaff : 0x2266aa;
    g.fillStyle(color);
    g.fillRect(x, y, w * ratio, h);

    g.lineStyle(1, 0x444466);
    g.strokeRect(x, y, w, h);
  }

  private drawXpBar(xp: number, xpToNext: number): void {
    const g = this.xpBarGraphics;
    g.clear();
    const x = 20;
    const y = 29;
    const w = 90;
    const h = 6;

    g.fillStyle(0x222222);
    g.fillRect(x, y, w, h);

    const ratio = xpToNext > 0 ? xp / xpToNext : 0;
    g.fillStyle(0xe8a038);
    g.fillRect(x, y, w * ratio, h);

    g.lineStyle(1, 0x444444);
    g.strokeRect(x, y, w, h);
  }

  private updateCombatHUD(stats: {
    hp: number; maxHp: number; coins: number; xp: number;
    xpToNext: number; level: number; swordLevel: number; swordDamage: number;
    energy: number; maxEnergy: number; comboCount: number; isBlocking: boolean;
  }): void {
    this.drawHpBar(stats.hp, stats.maxHp);
    this.drawEnergyBar(stats.energy, stats.maxEnergy);
    this.drawXpBar(stats.xp, stats.xpToNext);
    this.hpText.setText(`${stats.hp}/${stats.maxHp}`);
    this.energyText.setText(`${stats.energy}`);
    this.levelText.setText(`Lv.${stats.level}${stats.isBlocking ? ' [BLOCK]' : ''}`);
    this.coinText.setText(`${stats.coins} coins`);
  }

  private updateCombatHUDFromScene(): void {
    if (!this.gameScene.combatSystem) return;
    const stats = this.gameScene.combatSystem.getStats();
    this.updateCombatHUD(stats);
  }

  // ===== SHOP PANEL =====

  private createShopPanel(width: number, height: number): void {
    this.shopContainer = this.add.container(width / 2, height / 2);
    this.shopContainer.setVisible(false);
    this.shopContainer.setDepth(160);

    const panelW = 320;
    const panelH = 300;
    const bg = this.add.graphics();
    bg.fillStyle(0x1a2a1e, 0.95);
    bg.fillRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    bg.lineStyle(2, 0xe8a038);
    bg.strokeRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    this.shopContainer.add(bg);

    const title = this.add.text(0, -panelH / 2 + 15, 'Seed Shop', {
      fontFamily: 'monospace', fontSize: '16px', color: '#e8a038',
    });
    title.setOrigin(0.5, 0);
    this.shopContainer.add(title);

    const closeHint = this.add.text(0, panelH / 2 - 15, 'Press B to close', {
      fontFamily: 'monospace', fontSize: '10px', color: '#666666',
    });
    closeHint.setOrigin(0.5, 1);
    this.shopContainer.add(closeHint);

    // Close button
    const closeBtnBg = this.add.circle(panelW / 2 - 12, -panelH / 2 + 12, 14, 0xd4573b, 0.9);
    closeBtnBg.setStrokeStyle(1, 0xff6666);
    this.shopContainer.add(closeBtnBg);
    const closeX = this.add.text(panelW / 2 - 12, -panelH / 2 + 12, 'X', {
      fontFamily: 'monospace', fontSize: '14px', color: '#ffffff', fontStyle: 'bold',
    });
    closeX.setOrigin(0.5, 0.5);
    this.shopContainer.add(closeX);
    const closeHitZone = this.add.circle(panelW / 2 - 12, -panelH / 2 + 12, 18, 0x000000, 0.001);
    closeHitZone.setInteractive();
    closeHitZone.on('pointerdown', () => this.toggleShop());
    this.shopContainer.add(closeHitZone);

    // Shop items
    const startY = -panelH / 2 + 40;
    SHOP_ITEMS.forEach((item, i) => {
      const y = startY + i * 28;
      const itemBg = this.add.graphics();
      itemBg.fillStyle(0x333333, 0.5);
      itemBg.fillRoundedRect(-panelW / 2 + 10, y, panelW - 20, 24, 3);
      this.shopContainer.add(itemBg);

      const nameText = this.add.text(-panelW / 2 + 15, y + 4, item.name, {
        fontFamily: 'monospace', fontSize: '10px', color: '#ffffff',
      });
      this.shopContainer.add(nameText);

      const descText = this.add.text(-panelW / 2 + 15, y + 15, item.description, {
        fontFamily: 'monospace', fontSize: '7px', color: '#888888',
      });
      this.shopContainer.add(descText);

      const costText = this.add.text(panelW / 2 - 15, y + 8, `${item.cost} coins`, {
        fontFamily: 'monospace', fontSize: '9px', color: '#e8a038',
      });
      costText.setOrigin(1, 0);
      this.shopContainer.add(costText);

      // Buy button
      const buyBtn = this.add.text(panelW / 2 - 75, y + 4, 'BUY', {
        fontFamily: 'monospace', fontSize: '10px', color: '#6aaa3a',
        backgroundColor: '#2a3a2a',
        padding: { x: 4, y: 2 },
      });
      buyBtn.setInteractive({ useHandCursor: true });
      buyBtn.on('pointerdown', () => {
        this.gameScene.farmSystem.buyItem(item.id, item.cost, this.gameScene.player);
      });
      buyBtn.on('pointerover', () => buyBtn.setColor('#8bcc5a'));
      buyBtn.on('pointerout', () => buyBtn.setColor('#6aaa3a'));
      this.shopContainer.add(buyBtn);
    });
  }

  private toggleShop(): void {
    this.shopVisible = !this.shopVisible;
    this.shopContainer.setVisible(this.shopVisible);
  }

  showNotification(message: string): void {
    this.notificationText.setText(message);
    this.notificationText.setAlpha(1);

    if (this.notificationTween) {
      this.notificationTween.stop();
    }

    this.notificationTween = this.tweens.add({
      targets: this.notificationText,
      alpha: 0,
      duration: 2000,
      delay: 1500,
      ease: 'Power2',
    });
  }

  // ===== COMBO COUNTER =====

  private createComboCounter(width: number, height: number): void {
    this.comboContainer = this.add.container(width - 80, height / 2 - 40);
    this.comboContainer.setDepth(110);
    this.comboContainer.setAlpha(0);

    this.comboCountText = this.add.text(0, 0, '0', {
      fontFamily: 'monospace',
      fontSize: '32px',
      color: '#ffaa00',
      stroke: '#000000',
      strokeThickness: 5,
      fontStyle: 'bold',
    });
    this.comboCountText.setOrigin(0.5, 0.5);
    this.comboContainer.add(this.comboCountText);

    this.comboLabelText = this.add.text(0, 22, 'COMBO', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
    });
    this.comboLabelText.setOrigin(0.5, 0.5);
    this.comboContainer.add(this.comboLabelText);
  }

  private updateComboCounter(count: number, type: string): void {
    if (count <= 1) {
      // Fade out combo display
      if (this.comboFadeTween) this.comboFadeTween.stop();
      this.comboFadeTween = this.tweens.add({
        targets: this.comboContainer,
        alpha: 0,
        duration: 400,
        ease: 'Power2',
      });
      return;
    }

    // Show and update combo
    this.comboContainer.setAlpha(1);
    this.comboCountText.setText(`${count}`);

    // Color based on combo count
    if (count >= 10) {
      this.comboCountText.setColor('#ff4444');
      this.comboLabelText.setText('ULTRA COMBO!');
    } else if (count >= 7) {
      this.comboCountText.setColor('#ff44ff');
      this.comboLabelText.setText('SUPER COMBO!');
    } else if (count >= 4) {
      this.comboCountText.setColor('#44aaff');
      this.comboLabelText.setText('GREAT COMBO!');
    } else {
      this.comboCountText.setColor('#ffaa00');
      this.comboLabelText.setText('COMBO');
    }

    // Punch-in scale animation
    this.comboCountText.setScale(1.5);
    this.tweens.add({
      targets: this.comboCountText,
      scaleX: 1,
      scaleY: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    // Reset fade timer
    if (this.comboFadeTween) this.comboFadeTween.stop();
    this.comboFadeTween = this.tweens.add({
      targets: this.comboContainer,
      alpha: 0,
      duration: 600,
      delay: 1200,
      ease: 'Power2',
    });
  }

  // ===== MOVE LIST =====

  private createMoveList(width: number, height: number): void {
    this.moveListContainer = this.add.container(width / 2, height / 2);
    this.moveListContainer.setVisible(false);
    this.moveListContainer.setDepth(170);

    const panelW = 280;
    const panelH = 300;

    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1a, 0.95);
    bg.fillRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    bg.lineStyle(2, 0xffaa00);
    bg.strokeRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    this.moveListContainer.add(bg);

    const title = this.add.text(0, -panelH / 2 + 15, 'MOVE LIST', {
      fontFamily: 'monospace', fontSize: '16px', color: '#ffaa00', fontStyle: 'bold',
    });
    title.setOrigin(0.5, 0);
    this.moveListContainer.add(title);

    const moves = [
      { name: 'Light Attack', keys: 'J / Space', desc: 'Quick slash, fast recovery', color: '#ffffff' },
      { name: 'Heavy Attack', keys: 'K', desc: 'Powerful swing, wider range', color: '#ffaa00' },
      { name: 'Spin Slash', keys: 'L', desc: '360° area attack (25 EN)', color: '#44ff44' },
      { name: 'Hadouken', keys: '↓→ + L', desc: 'Ranged energy blast (30 EN)', color: '#44aaff' },
      { name: 'Shoryuken', keys: '→↓ + L', desc: 'Dash strike, invulnerable (40 EN)', color: '#ff44ff' },
      { name: 'Block', keys: 'Hold Shift', desc: 'Reduce damage by 75%', color: '#6688cc' },
      { name: 'Dodge Roll', keys: 'Shift + Direction', desc: 'Invulnerable dash (1.5s CD)', color: '#aaddff' },
      { name: 'Combos', keys: 'Chain J/K hits', desc: 'Up to 1.5x damage at 10 hits!', color: '#ff4444' },
      { name: 'Sentinel', keys: 'F (face plant)', desc: 'Toggle plant auto-defense mode', color: '#88ff88' },
    ];

    const startY = -panelH / 2 + 42;
    moves.forEach((move, i) => {
      const y = startY + i * 24;

      const rowBg = this.add.graphics();
      rowBg.fillStyle(i % 2 === 0 ? 0x111122 : 0x0a0a1a, 0.5);
      rowBg.fillRect(-panelW / 2 + 5, y, panelW - 10, 24);
      this.moveListContainer.add(rowBg);

      const nameText = this.add.text(-panelW / 2 + 10, y + 3, move.name, {
        fontFamily: 'monospace', fontSize: '10px', color: move.color, fontStyle: 'bold',
      });
      this.moveListContainer.add(nameText);

      const keyText = this.add.text(panelW / 2 - 10, y + 3, move.keys, {
        fontFamily: 'monospace', fontSize: '9px', color: '#cccccc',
      });
      keyText.setOrigin(1, 0);
      this.moveListContainer.add(keyText);

      const descText = this.add.text(-panelW / 2 + 10, y + 14, move.desc, {
        fontFamily: 'monospace', fontSize: '7px', color: '#888888',
      });
      this.moveListContainer.add(descText);
    });

    const closeHint = this.add.text(0, panelH / 2 - 12, 'Press M to close', {
      fontFamily: 'monospace', fontSize: '9px', color: '#666666',
    });
    closeHint.setOrigin(0.5, 1);
    this.moveListContainer.add(closeHint);
  }

  private toggleMoveList(): void {
    this.moveListVisible = !this.moveListVisible;
    this.moveListContainer.setVisible(this.moveListVisible);
  }

  // ===== CONNECTION STATUS =====

  private createConnectionStatus(width: number): void {
    const y = 48;
    this.connectionDot = this.add.circle(width - 98, y, 4, 0xe8a038, 1);
    this.connectionDot.setDepth(100);

    this.connectionStatusText = this.add.text(width - 90, y, 'Connecting...', {
      fontFamily: 'monospace', fontSize: '8px', color: '#e8a038',
    });
    this.connectionStatusText.setOrigin(0, 0.5);
    this.connectionStatusText.setDepth(100);
  }

  private updateConnectionStatus(state: string): void {
    if (!this.connectionStatusText || !this.connectionDot) return;

    switch (state) {
      case 'connected':
        this.connectionDot.setFillStyle(0x6aaa3a, 1);
        this.connectionStatusText.setText('Online');
        this.connectionStatusText.setColor('#6aaa3a');
        break;
      case 'connecting':
        this.connectionDot.setFillStyle(0xe8a038, 1);
        this.connectionStatusText.setText('Connecting...');
        this.connectionStatusText.setColor('#e8a038');
        break;
      case 'reconnecting':
        this.connectionDot.setFillStyle(0xe8a038, 1);
        this.connectionStatusText.setText('Reconnecting...');
        this.connectionStatusText.setColor('#e8a038');
        break;
      case 'disconnected':
        this.connectionDot.setFillStyle(0xd4573b, 1);
        this.connectionStatusText.setText('Offline');
        this.connectionStatusText.setColor('#d4573b');
        break;
    }
  }

  // ===== MINIMAP =====

  private createMinimap(width: number): void {
    const size = this.minimapSize;
    // Position below the time display (which is at top-right)
    const mapX = width - size - 10;
    const mapY = 55;

    this.minimapScale = size / (MAP_WIDTH * TILE_SIZE);

    this.minimapContainer = this.add.container(mapX, mapY);
    this.minimapContainer.setDepth(90);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.7);
    bg.fillRoundedRect(-2, -2, size + 4, size + 4, 4);
    bg.lineStyle(1, 0x666666, 0.8);
    bg.strokeRoundedRect(-2, -2, size + 4, size + 4, 4);
    this.minimapContainer.add(bg);

    // Static terrain layer (rendered once)
    this.minimapGraphics = this.add.graphics();
    this.minimapContainer.add(this.minimapGraphics);
    this.renderMinimapTerrain();

    // Player dot (green, pulsing)
    this.minimapPlayerDot = this.add.circle(size / 2, size / 2, 3, 0x6aaa3a, 1);
    this.minimapPlayerDot.setStrokeStyle(1, 0xffffff, 0.8);
    this.minimapContainer.add(this.minimapPlayerDot);

    // Pulse animation on player dot
    this.tweens.add({
      targets: this.minimapPlayerDot,
      scaleX: 1.4,
      scaleY: 1.4,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Tap the minimap to expand/collapse (for mobile)
    const mapHitZone = this.add.zone(size / 2, size / 2, size + 8, size + 8);
    mapHitZone.setInteractive();
    mapHitZone.on('pointerdown', () => {
      // Toggle between small (100) and expanded (180) minimap
      if (this.minimapSize === 100) {
        this.minimapSize = 180;
      } else {
        this.minimapSize = 100;
      }
      // Rebuild minimap at new size
      const wasVisible = this.minimapVisible;
      this.minimapContainer.destroy();
      this.createMinimap(this.cameras.main.width);
      this.minimapContainer.setVisible(wasVisible);
    });
    this.minimapContainer.add(mapHitZone);

    // Label
    const label = this.add.text(size / 2, size + 4, 'Tap to expand', {
      fontFamily: 'monospace', fontSize: '6px', color: '#888888',
    });
    label.setOrigin(0.5, 0);
    this.minimapContainer.add(label);
  }

  private renderMinimapTerrain(): void {
    const g = this.minimapGraphics;
    g.clear();

    const s = this.minimapScale;
    const ts = TILE_SIZE;
    const cx = Math.floor(MAP_WIDTH / 2);
    const cy = Math.floor(MAP_HEIGHT / 2);
    const farmHalf = FARM_SIZE / 2;

    // Base grass
    g.fillStyle(0x3a6a2a);
    g.fillRect(0, 0, this.minimapSize, this.minimapSize);

    // Dark forest — northwest
    g.fillStyle(0x2a3a20);
    g.fillRect(0, 0, this.minimapSize * 0.35, this.minimapSize * 0.4);

    // Marsh — southwest
    g.fillStyle(0x4a6a3a);
    g.fillRect(0, this.minimapSize * 0.6, this.minimapSize * 0.3, this.minimapSize * 0.35);

    // Rocky highlands — northeast
    g.fillStyle(0x7a7060);
    g.fillRect(this.minimapSize * 0.7, 0, this.minimapSize * 0.25, this.minimapSize * 0.35);

    // Meadow — southeast
    g.fillStyle(0x5aaa4a);
    g.fillRect(this.minimapSize * 0.55, this.minimapSize * 0.55, this.minimapSize * 0.4, this.minimapSize * 0.4);

    // Farm area (brown)
    const farmPxX = (cx - farmHalf) * ts * s;
    const farmPxY = (cy - farmHalf) * ts * s;
    const farmPxW = FARM_SIZE * ts * s;
    g.fillStyle(0x8b6c3a);
    g.fillRect(farmPxX, farmPxY, farmPxW, farmPxW);

    // Main river — N-S through western third
    g.fillStyle(0x4488cc);
    for (let y = 0; y < MAP_HEIGHT; y += 3) {
      const riverX = cx - 30 + Math.floor(Math.sin(y * 0.04) * 12 + Math.sin(y * 0.11) * 5);
      const px = riverX * ts * s;
      const py = y * ts * s;
      g.fillRect(px - 2 * ts * s, py, 4 * ts * s, 3 * ts * s);
    }

    // Tributary — E-W through south
    for (let x = Math.floor(cx - 25); x < MAP_WIDTH - 15; x += 3) {
      const riverY = cy + 40 + Math.floor(Math.sin(x * 0.06) * 8);
      const px = x * ts * s;
      const py = riverY * ts * s;
      g.fillRect(px, py - 1 * ts * s, 3 * ts * s, 2 * ts * s);
    }

    // Northeast lake
    const lake1PX = (cx + 55) * ts * s;
    const lake1PY = (cy - 40) * ts * s;
    g.fillCircle(lake1PX, lake1PY, 8 * ts * s);

    // Southwest pond
    const pond1PX = (cx - 50) * ts * s;
    const pond1PY = (cy + 50) * ts * s;
    g.fillCircle(pond1PX, pond1PY, 5 * ts * s);

    // Pond near farm
    const pond2PX = (cx + 25) * ts * s;
    const pond2PY = (cy + 15) * ts * s;
    g.fillCircle(pond2PX, pond2PY, 4 * ts * s);

    // Sand edges
    g.fillStyle(0xc2a64e);
    const edgeW = 5 * ts * s;
    g.fillRect(0, 0, this.minimapSize, edgeW);
    g.fillRect(0, this.minimapSize - edgeW, this.minimapSize, edgeW);
    g.fillRect(0, 0, edgeW, this.minimapSize);
    g.fillRect(this.minimapSize - edgeW, 0, edgeW, this.minimapSize);

    // Thornwick's farm (red-ish brown)
    const rivalX = Math.floor(MAP_WIDTH * 0.75);
    const rivalY = Math.floor(MAP_HEIGHT * 0.25);
    const rivalPxX = rivalX * ts * s;
    const rivalPxY = rivalY * ts * s;
    const rivalSize = 6 * ts * s;
    g.fillStyle(0x8b4a3a);
    g.fillRect(rivalPxX, rivalPxY, rivalSize, rivalSize);
  }

  private updateMinimap(): void {
    if (!this.minimapVisible || !this.gameScene.player) return;

    const s = this.minimapScale;
    const px = this.gameScene.player.sprite.x * s;
    const py = this.gameScene.player.sprite.y * s;
    this.minimapPlayerDot.setPosition(px, py);

    // Update dynamic elements: draw monster dots and rival dot on a separate pass
    // We reuse the player dot's parent container for dynamic overlays
    // Remove old dynamic dots (tagged with name 'minimap-dynamic')
    const toRemove: Phaser.GameObjects.GameObject[] = [];
    this.minimapContainer.iterate((child: Phaser.GameObjects.GameObject) => {
      if (child.name === 'minimap-dynamic') {
        toRemove.push(child);
      }
    });
    toRemove.forEach(c => { c.destroy(); });

    // Draw monster dots (red)
    if (this.gameScene.nightMonsterSystem) {
      for (const monster of this.gameScene.nightMonsterSystem.monsters) {
        if (monster.dead || !monster.sprite.active) continue;
        const mx = monster.sprite.x * s;
        const my = monster.sprite.y * s;
        const dot = this.add.circle(mx, my, 1.5, 0xd4573b, 0.9);
        dot.name = 'minimap-dynamic';
        this.minimapContainer.add(dot);
      }
    }

    // Draw rival dot (orange)
    if (this.gameScene.aiRivalSystem?.rivalSprite) {
      const rx = this.gameScene.aiRivalSystem.rivalSprite.x * s;
      const ry = this.gameScene.aiRivalSystem.rivalSprite.y * s;
      const rivalDot = this.add.circle(rx, ry, 2, 0xdd5533, 0.9);
      rivalDot.name = 'minimap-dynamic';
      this.minimapContainer.add(rivalDot);
    }
  }

  private toggleMinimap(): void {
    this.minimapVisible = !this.minimapVisible;
    this.minimapContainer.setVisible(this.minimapVisible);
  }

  // ===== DAWN REPORT SCREEN =====

  showDawnReport(data: {
    nightNumber: number;
    monstersKilled: number;
    sentinelsLost: number;
    coinsEarned: number;
    milestones: { description: string; completed: boolean }[];
    milestonesCompleted: number;
    milestonesTotal: number;
    bonusCoins: number;
    bonusFertilizer: number;
    bonusSeed?: string;
    forecast: string;
    forecastTip: string;
  }): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    const container = this.add.container(width / 2, height / 2);
    container.setDepth(500);
    container.setAlpha(0);

    const panelW = 360;
    const panelH = 320;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.95);
    bg.fillRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    bg.lineStyle(2, 0xe8a038);
    bg.strokeRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    container.add(bg);

    // Title
    const title = this.add.text(0, -panelH / 2 + 12, `NIGHT ${data.nightNumber} SURVIVED`, {
      fontFamily: 'monospace', fontSize: '14px', color: '#e8a038', fontStyle: 'bold',
    });
    title.setOrigin(0.5, 0);
    container.add(title);

    // Divider
    const divider = this.add.graphics();
    divider.lineStyle(1, 0x666666, 0.5);
    divider.lineBetween(-panelW / 2 + 15, -panelH / 2 + 32, panelW / 2 - 15, -panelH / 2 + 32);
    container.add(divider);

    // Stats
    let y = -panelH / 2 + 42;
    const leftX = -panelW / 2 + 20;
    const rightX = panelW / 2 - 20;

    const addStatLine = (label: string, value: string, color: string = '#dddddd') => {
      const labelText = this.add.text(leftX, y, label, {
        fontFamily: 'monospace', fontSize: '11px', color: '#aaaaaa',
      });
      container.add(labelText);
      const valueText = this.add.text(rightX, y, value, {
        fontFamily: 'monospace', fontSize: '11px', color,
      });
      valueText.setOrigin(1, 0);
      container.add(valueText);
      y += 18;
    };

    addStatLine('Monsters Killed:', `${data.monstersKilled}`, '#ffffff');
    addStatLine('Sentinels Lost:', `${data.sentinelsLost}`, data.sentinelsLost === 0 ? '#6aaa3a' : '#d4573b');
    addStatLine('Coins Earned:', `${data.coinsEarned}`, '#e8a038');

    // Milestones section
    y += 4;
    const milestoneHeader = this.add.text(leftX, y, `Milestones: ${data.milestonesCompleted}/${data.milestonesTotal}`, {
      fontFamily: 'monospace', fontSize: '10px', color: '#e8a038',
    });
    container.add(milestoneHeader);
    y += 16;

    for (const m of data.milestones) {
      const checkmark = m.completed ? '\u2713' : '\u2717';
      const mColor = m.completed ? '#6aaa3a' : '#666666';
      const mText = this.add.text(leftX + 5, y, `${checkmark} ${m.description}`, {
        fontFamily: 'monospace', fontSize: '9px', color: mColor,
      });
      container.add(mText);
      y += 14;
    }

    // Dawn bonus
    if (data.milestonesCompleted >= 2) {
      y += 4;
      let bonusStr = `Dawn Bonus: +${data.bonusCoins} coins, +${data.bonusFertilizer} fertilizer`;
      if (data.bonusSeed) {
        const seedName = PLANTS[data.bonusSeed]?.name ?? data.bonusSeed;
        bonusStr += `, +1 ${seedName} seed`;
      }
      const bonusText = this.add.text(0, y, bonusStr, {
        fontFamily: 'monospace', fontSize: '9px', color: '#6aaa3a',
      });
      bonusText.setOrigin(0.5, 0);
      container.add(bonusText);
      y += 16;
    }

    // Forecast
    y += 4;
    const divider2 = this.add.graphics();
    divider2.lineStyle(1, 0x666666, 0.5);
    divider2.lineBetween(-panelW / 2 + 15, y, panelW / 2 - 15, y);
    container.add(divider2);
    y += 8;

    const forecastLabel = this.add.text(leftX, y, 'TONIGHT\'S FORECAST:', {
      fontFamily: 'monospace', fontSize: '9px', color: '#d4573b', fontStyle: 'bold',
    });
    container.add(forecastLabel);
    y += 14;

    const forecastText = this.add.text(leftX, y, data.forecast, {
      fontFamily: 'monospace', fontSize: '9px', color: '#dddddd',
      wordWrap: { width: panelW - 40 },
    });
    container.add(forecastText);
    y += 16;

    const tipText = this.add.text(leftX, y, `Tip: ${data.forecastTip}`, {
      fontFamily: 'monospace', fontSize: '8px', color: '#888888',
      wordWrap: { width: panelW - 40 },
    });
    container.add(tipText);

    // Continue button
    const btnY = panelH / 2 - 22;
    const btnBg = this.add.graphics();
    btnBg.fillStyle(0x6aaa3a, 0.8);
    btnBg.fillRoundedRect(-60, btnY - 12, 120, 24, 4);
    btnBg.lineStyle(1, 0xffffff, 0.3);
    btnBg.strokeRoundedRect(-60, btnY - 12, 120, 24, 4);
    container.add(btnBg);

    const btnText = this.add.text(0, btnY, `Continue to Day ${data.nightNumber + 1}`, {
      fontFamily: 'monospace', fontSize: '10px', color: '#ffffff', fontStyle: 'bold',
    });
    btnText.setOrigin(0.5, 0.5);
    container.add(btnText);

    const btnZone = this.add.zone(0, btnY, 120, 24).setInteractive({ useHandCursor: true });
    container.add(btnZone);

    const closeDawnReport = () => {
      this.tweens.add({
        targets: container,
        alpha: 0,
        duration: 300,
        onComplete: () => container.destroy(),
      });
      window.removeEventListener('keydown', keyClose);
    };

    btnZone.on('pointerdown', closeDawnReport);

    // Also close on Space/Enter/E
    const keyClose = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'KeyE') {
        e.preventDefault();
        closeDawnReport();
      }
    };
    window.addEventListener('keydown', keyClose);

    // Fade in
    this.tweens.add({
      targets: container,
      alpha: 1,
      duration: 500,
      ease: 'Power2',
    });
  }
}
